/**
 * ACTIVITY-CORE — the feed's DATA half, extracted from the page component.
 *
 * Solid-dependent, so it is exported from `@agape/shared/solid`, not from the
 * main barrel. It owns the local (PowerSync mirror) query, the deep-history
 * pages fetched from the API, and the merge between them.
 *
 * Pure presentation (grouping, human labels, undo detection) stays in
 * `@agape/shared` (`format.ts`).
 */
import { type Accessor, createMemo, createSignal } from "solid-js";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB } from "@agape/powersync";
import { createPowerSyncQuery } from "@agape/shared/solid";
import {
  type ActivityFeedFilter,
  getActivityFeedQuery,
  queryKeys,
  type ActivityRow,
} from "@agape/shared";

export type { ActivityRow };

/**
 * Shape of one API row (camelCase) as served by `GET /api/activity`.
 * Mirrors `core_rust::models::activity::Activity` without dragging the Rust
 * generator into the browser.
 */
export interface ApiActivityRow {
  id: string;
  scopeId?: string | null;
  listId?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  action?: string | null;
  actorUserId?: string | null;
  actorName?: string | null;
  actorDeviceId?: string | null;
  platform?: string | null;
  serverTs?: string | null;
  clientTs?: string | null;
  patch?: unknown;
  batchId?: string | null;
  revertsActivityId?: string | null;
}

/**
 * Maps a camelCase API row to the snake_case `ActivityRow` the UI components
 * expect. Re-exported so other consumers don't reinvent the camel-to-snake.
 */
export function mapApiActivityRow(row: ApiActivityRow): ActivityRow {
  return {
    id: row.id,
    scope_id: row.scopeId ?? null,
    entity_type: row.entityType ?? null,
    entity_id: row.entityId ?? null,
    action: row.action ?? null,
    actor_user_id: row.actorUserId ?? null,
    actor_name: row.actorName ?? null,
    actor_device_id: row.actorDeviceId ?? null,
    platform: row.platform ?? null,
    server_ts: row.serverTs ?? null,
    client_ts: row.clientTs ?? null,
    patch: typeof row.patch === "string" ? row.patch : row.patch ? JSON.stringify(row.patch) : null,
    batch_id: row.batchId ?? null,
    reverts_activity_id: row.revertsActivityId ?? null,
  };
}

export interface ActivityHistoryPage {
  rows: ActivityRow[];
  hasMore: boolean;
}

/**
 * What the host has to hand the data source. None of this is hardcoded, so
 * targets with different service topologies supply only what they have.
 */
export interface ActivityFeedSourceDeps {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  /**
   * App-specific narrowing of the feed. Reactive: changing it re-runs
   * the local query and resets nothing else.
   */
  filter?: Accessor<Partial<ActivityFeedFilter>>;
  /**
   * Deep history. Returns `null` on failure — reporting it to the user is the
   * host's job, not this module's.
   */
  fetchOlder: (
    before: string | null,
    limit: number,
    filter: Partial<ActivityFeedFilter>,
  ) => Promise<ActivityHistoryPage | null>;
  /**
   * Keeps a deep-history page consistent with the active filter. The API is
   * asked to filter, but a page fetched under an older filter is still in
   * memory; without this it would leak into the narrowed view.
   */
  matchesFilter?: (row: ActivityRow, filter: Partial<ActivityFeedFilter>) => boolean;
  /** Rows kept from the local mirror. */
  localLimit?: number;
  /** Rows per API page. */
  pageSize?: number;
}

export const DEFAULT_LOCAL_FEED_LIMIT = 200;
export const DEFAULT_API_PAGE_SIZE = 50;

export function createActivityFeedSource(deps: ActivityFeedSourceDeps): ActivityFeedSource {
  const localLimit = deps.localLimit ?? DEFAULT_LOCAL_FEED_LIMIT;
  const pageSize = deps.pageSize ?? DEFAULT_API_PAGE_SIZE;

  const [olderRows, setOlderRows] = createSignal<ActivityRow[]>([]);
  const [loadingOlder, setLoadingOlder] = createSignal(false);
  const [hasMoreOlder, setHasMoreOlder] = createSignal(true);

  const filter = () => deps.filter?.() ?? {};

  const localQuery = createPowerSyncQuery<ActivityRow>({
    queryKey: () => queryKeys.activity.feed(deps.userId(), filter()),
    db: () => deps.db() ?? null,
    queryFn: () => {
      const dbInstance = deps.drizzle();
      return dbInstance
        ? getActivityFeedQuery(dbInstance, { limit: localLimit, ...filter() })
        : null;
    },
    enabled: () => Boolean(deps.drizzle()),
  });

  const rows = createMemo<ActivityRow[]>(() => {
    const local = localQuery.data ?? [];
    if (olderRows().length === 0) {
      return local;
    }
    const active = filter();
    const older = deps.matchesFilter
      ? olderRows().filter((r) => deps.matchesFilter?.(r, active) ?? true)
      : olderRows();
    const seen = new Set(local.map((r) => r.id));
    return [...local, ...older.filter((r) => !seen.has(r.id))];
  });

  const loadOlder = async () => {
    if (loadingOlder()) {
      return;
    }
    setLoadingOlder(true);
    try {
      const current = rows();
      const last = current.at(-1);
      const page = await deps.fetchOlder(last?.id ?? null, pageSize, filter());
      if (!page) {
        return;
      }
      setOlderRows((prev) => [...prev, ...page.rows]);
      setHasMoreOlder(page.hasMore);
    } finally {
      setLoadingOlder(false);
    }
  };

  return {
    rows,
    isLoading: () => localQuery.isFetching && (localQuery.data ?? []).length === 0,
    isLoadingOlder: loadingOlder,
    hasMore: () => rows().length >= pageSize && hasMoreOlder(),
    loadOlder,
    pageSize,
  };
}

export interface ActivityFeedSource {
  rows: Accessor<ActivityRow[]>;
  isLoading: Accessor<boolean>;
  isLoadingOlder: Accessor<boolean>;
  hasMore: Accessor<boolean>;
  loadOlder: () => Promise<void>;
  pageSize: number;
}
